import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AiCreditsDto, AiReportResult } from '@order-pilot/shared';
import { ApiError, getErrorMessage } from '@/lib/api';
import { askPilotKeys, askQuestion } from './api';
import { ReportResult } from './ReportResult';

const CHIPS = [
  'Sales by branch this month',
  'Top 10 items sold this week',
  'Busiest hours this week',
  'Sales by payment method, last 30 days',
  'Expenses by category, last month',
  'Sales by day, last 7 days',
];
const CANT_TRY = ['Sales by order type this month', 'Item sales by category, this week'];

type Entry =
  | { id: number; question: string; state: 'thinking' }
  | { id: number; question: string; state: 'answer'; result: AiReportResult; saved?: boolean }
  | { id: number; question: string; state: 'cannot'; message: string }
  | { id: number; question: string; state: 'error'; message: string; code?: string };

export interface AskPilotHandle {
  /** Show a saved report in the thread; it was run already and cost nothing. */
  showSaved: (title: string, result: AiReportResult) => void;
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

/**
 * While Claude works (2–5 s): status lines that move on a timer and a chart
 * skeleton. The lines describe the steps that really happen, in order.
 */
function Thinking() {
  const steps = ['Reading your question', 'Choosing the figures', 'Running the report on your data', 'Building the chart'];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setStep((s) => Math.min(s + 1, steps.length - 1)), 1100);
    return () => window.clearInterval(t);
  }, [steps.length]);
  return (
    <div className="card flex flex-col gap-4 p-5" role="status" aria-live="polite">
      <div className="flex flex-col gap-2 text-sm">
        {steps.slice(0, step).map((s) => (
          <div key={s} className="flex gap-2 text-slate-500">
            <span className="text-emerald-600">✓</span>
            {s}
          </div>
        ))}
        <div className="flex items-center gap-2 text-slate-900">
          <span aria-hidden="true" className="animate-pulse text-brand-600">✦</span>
          {steps[step]}…
        </div>
      </div>
      <div className="flex h-32 items-end gap-3 border-b border-slate-200 px-1">
        {[70, 92, 48, 60].map((h, i) => (
          <div key={i} className="flex-1 animate-pulse rounded-t-md bg-slate-200/80" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

function Notice({ icon, title, children }: { icon: string; title: string; children?: ReactNode }) {
  return (
    <div className="card flex items-start gap-3 p-5">
      <div className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-brand-600/10 text-brand-700" aria-hidden="true">
        {icon}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <p className="font-semibold text-slate-900">{title}</p>
        {children}
      </div>
    </div>
  );
}

const Chip = ({ text, onPick, disabled }: { text: string; onPick: (q: string) => void; disabled?: boolean }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onPick(text)}
    className="rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 transition hover:border-brand-600/60 disabled:opacity-50"
  >
    {text}
  </button>
);

export const AskPilot = forwardRef<AskPilotHandle, { credits: AiCreditsDto }>(function AskPilot({ credits }, ref) {
  const qc = useQueryClient();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [text, setText] = useState('');
  const nextId = useRef(1);
  const input = useRef<HTMLTextAreaElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const busy = entries.some((e) => e.state === 'thinking');
  const locked = !credits.enabled;
  const off = !credits.configured;
  const empty = !locked && credits.available <= 0;
  const disabled = locked || off || empty;

  useImperativeHandle(ref, () => ({
    showSaved: (title, result) =>
      setEntries((list) => [...list, { id: nextId.current++, question: title, state: 'answer', result, saved: true }]),
  }));

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [entries]);

  const ask = async (raw: string) => {
    const question = raw.trim();
    if (question.length < 3 || busy || disabled) return;
    const id = nextId.current++;
    setText('');
    setEntries((list) => [...list, { id, question, state: 'thinking' }]);
    let next: Entry;
    try {
      const reply = await askQuestion(question);
      next = 'result' in reply ? { id, question, state: 'answer', result: reply.result } : { id, question, state: 'cannot', message: reply.cannot };
    } catch (e) {
      const code = e instanceof ApiError ? ((e.details as { code?: string } | undefined)?.code ?? String(e.status)) : undefined;
      next = { id, question, state: 'error', code, message: getErrorMessage(e, 'The AI did not answer. Try again; no credit was used.') };
    }
    setEntries((list) => list.map((x) => (x.id === id ? next : x)));
    void qc.invalidateQueries({ queryKey: askPilotKeys.credits });
  };

  const hint = locked ? 'Not on your plan' : off ? 'Not set up yet' : empty ? '0 credits left' : busy ? 'Working…' : 'Enter to ask · 1 credit';
  const placeholder = locked
    ? 'Available on Growth, Business and Custom'
    : off
      ? 'Ask Pilot is not set up on this server yet'
      : empty
        ? 'Add credits to ask'
        : entries.length
          ? 'Ask a follow-up…'
          : 'Ask about sales, items, expenses, returns or purchases…';

  return (
    <div className="flex min-h-[calc(100vh-12rem)] flex-col">
      <div className={`mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 ${entries.length || locked ? 'justify-end' : 'justify-center'}`}>
        {locked ? (
          <Notice icon="✦" title="Upgrade to Growth or above to ask your own questions">
            <p className="text-sm leading-relaxed text-slate-600">
              Ask about sales, items, branches and expenses in plain English and get a chart and a table back. Ask Pilot comes with
              the Growth (100 questions a month), Business and Custom (300 a month) plans. Contact your Order Pilot account manager to
              upgrade. Your saved reports keep working on every plan.
            </p>
          </Notice>
        ) : entries.length === 0 ? (
          <div className="flex flex-col gap-5 py-6">
            <div className="flex flex-col gap-2.5">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600/10 text-lg text-brand-700" aria-hidden="true">
                ✦
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{greeting()}. What would you like to know?</h2>
              <p className="text-sm leading-relaxed text-slate-600">
                Ask about sales, item sales, returns, expenses or purchases, by branch, day, hour or category, for any period from today
                to this year. Every answer comes back as a chart and a table.
              </p>
            </div>
            {!disabled ? (
              <div className="flex flex-wrap gap-2">
                {CHIPS.map((c) => (
                  <Chip key={c} text={c} onPick={ask} />
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {entries.map((e) => (
          <div key={e.id} className="flex flex-col gap-3">
            <div className="max-w-[80%] self-end rounded-2xl rounded-br-sm border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm text-slate-900">
              {e.state === 'answer' && e.saved ? <span className="mr-1.5 text-xs text-slate-500">Saved report ·</span> : null}
              {e.question}
            </div>
            {e.state === 'thinking' ? (
              <Thinking />
            ) : e.state === 'answer' ? (
              <ReportResult result={e.result} question={e.question} saved={e.saved} onFollowUp={() => input.current?.focus()} />
            ) : e.state === 'cannot' ? (
              <Notice icon="✦" title="Reports can't show that yet">
                <p className="text-sm leading-relaxed text-slate-600">{e.message} No credit was used.</p>
                <p className="mt-2 text-xs text-slate-500">Try one of these instead</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {CANT_TRY.map((c) => (
                    <Chip key={c} text={c} onPick={ask} disabled={busy} />
                  ))}
                </div>
              </Notice>
            ) : e.code === 'NO_CREDITS' ? (
              <Notice icon="◐" title="No AI credits left this month">
                <p className="text-sm leading-relaxed text-slate-600">
                  Boost packs add more: +100 credits for ₹299 or +500 for ₹1,199, and boost credits never expire. Contact your Order
                  Pilot account manager to add one. Your monthly credits renew on the 1st, and saved reports still open for free.
                </p>
              </Notice>
            ) : (
              <Notice icon="↻" title={e.code === 'DAILY_CAP' ? "Today's limit is reached" : 'The AI did not answer'}>
                <p className="text-sm leading-relaxed text-slate-600">{e.message}</p>
                {e.code !== 'DAILY_CAP' ? (
                  <div className="mt-2">
                    <button type="button" disabled={busy} onClick={() => ask(e.question)} className="btn btn-sm btn-primary">
                      Try again
                    </button>
                  </div>
                ) : null}
              </Notice>
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>

      <div className="sticky bottom-0 mx-auto mt-5 w-full max-w-3xl bg-gradient-to-t from-surface via-surface pb-3 pt-2">
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            void ask(text);
          }}
          className={`flex flex-col gap-2.5 rounded-xl border border-slate-300 bg-white py-2.5 pl-4 pr-3 shadow-sm focus-within:border-brand-600 ${disabled ? 'opacity-60' : ''}`}
        >
          <textarea
            ref={input}
            rows={1}
            maxLength={500}
            value={text}
            disabled={disabled}
            placeholder={busy ? 'Ask Pilot is working on your answer…' : placeholder}
            aria-label="Ask Pilot"
            onChange={(ev) => setText(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' && !ev.shiftKey) {
                ev.preventDefault();
                void ask(text);
              }
            }}
            className="max-h-40 min-h-[24px] resize-none border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{hint}</span>
            <button
              type="submit"
              aria-label="Ask"
              disabled={disabled || busy || text.trim().length < 3}
              className={`ml-auto grid h-8 w-8 place-items-center rounded-lg text-base transition ${
                text.trim().length >= 3 && !busy && !disabled ? 'bg-brand-700 text-white' : 'bg-slate-100 text-slate-400'
              }`}
            >
              ↑
            </button>
          </div>
        </form>
        <p className="mt-2 hidden text-center text-xs text-slate-500 sm:block">
          1 question uses 1 credit. Ask Pilot can&apos;t report on individual customers or staff.
        </p>
      </div>
    </div>
  );
});
